import { dayjs } from '@tunasong/models'
import type { Poll, PollOption } from '@tunasong/schemas'
import { isDateSlot } from '@tunasong/schemas'
import { Button, ConfirmDialog, DatePicker, TextField, TimePicker, Typography } from '@tunasong/ui-lib'
import type { Dayjs } from 'dayjs'
import type { ChangeEvent, FC } from 'react'
import { useState } from 'react'
import { formatSlotLabel, getNextSlotStart, toRespondByInstant } from './date-slot.js'
import styles from './slot-list-editor.module.css'

export interface SlotListEditorProps {
  element: Poll
  /** Answers per option index, from `getAnswerCounts`. An untouched index is absent. */
  answerCounts: Map<number, number>
  onChange(poll: Poll): void
}

/**
 * Owner authoring for a rehearsal poll: the slot list, `Dates needed`, and `Respond by`.
 *
 * `Dates needed` is deliberately NOT the vote-quota slider. `responseQuota` caps how many
 * options a *respondent* may pick; `datesNeeded` is a property of the poll's outcome. Two
 * different axes, and the quota control has no place on this surface.
 */
export const SlotListEditor: FC<SlotListEditorProps> = props => {
  const { element, answerCounts, onChange } = props

  const [pendingRemoval, setPendingRemoval] = useState<number | undefined>(undefined)

  /**
   * Local draft of the poll being authored.
   *
   * `useEntityUpdate` accumulates into a ref and only calls the API after its debounce — it
   * never writes the Redux cache (`rtk-redux.hook.ts:153-158`). So `element` keeps the old
   * value for up to a second after every edit, which broke two things:
   *
   * - a controlled input reconciled back to the stale value on any re-render, and the user's
   *   typed digit or picked date vanished;
   * - worse, `updateSlotTime` recombines against `element`, so a date change followed by a
   *   time change wrote the new time onto the OLD date and silently discarded the date edit —
   *   landing the slot on a day nobody chose.
   *
   * Reading and writing through one draft makes both correct. The draft is per-edit-session,
   * so a concurrent external change is masked until remount — the same trade-off the
   * pre-existing option field already makes with `defaultValue`.
   */
  const [draft, setDraft] = useState<Poll | undefined>(undefined)
  const current = draft ?? element

  const commit = (next: Poll) => {
    setDraft(next)
    onChange(next)
  }

  const answerCount = (idx: number) => answerCounts.get(idx) ?? 0
  const hasAnyAnswers = answerCounts.size > 0

  const writeSlot = (idx: number, patch: Partial<PollOption>) =>
    commit({
      ...current,
      pollOptions: current.pollOptions.map((option, i) => (i === idx ? { ...option, ...patch } : option)),
    })

  /**
   * `DatePicker` hands back midnight local, so take only the calendar day and keep the slot's
   * time. Storing the picker's value whole would wipe the time.
   */
  const updateSlotDate = (idx: number) => (value: Dayjs | null) => {
    if (!value) {
      return
    }
    const start = dayjs(current.pollOptions[idx].start).year(value.year()).month(value.month()).date(value.date())
    writeSlot(idx, { start: start.toISOString() })
  }

  /**
   * `TimePicker` hands back TODAY with the chosen h:mm, so take only the time and keep the
   * slot's date. Storing the picker's value whole would move a March rehearsal to today.
   */
  const updateSlotTime = (idx: number) => (value: Dayjs | null) => {
    if (!value) {
      return
    }
    const start = dayjs(current.pollOptions[idx].start)
      .hour(value.hour())
      .minute(value.minute())
      .second(0)
      .millisecond(0)
    writeSlot(idx, { start: start.toISOString() })
  }

  const handleAdd = () => {
    const live = current.pollOptions.filter(option => !option.removed)
    commit({
      ...current,
      pollOptions: [...current.pollOptions, { name: '', start: getNextSlotStart(live.at(-1)?.start) }],
    })
  }

  /** Never splice, and never un-set `removed` — see the tombstone rule in the spec. */
  const tombstone = (idx: number) => writeSlot(idx, { removed: true })

  const handleRemoveClick = (idx: number) => () => {
    if (answerCount(idx) > 0) {
      setPendingRemoval(idx)
      return
    }
    tombstone(idx)
  }

  const handleConfirmRemoval = () => {
    if (typeof pendingRemoval === 'number') {
      tombstone(pendingRemoval)
    }
    setPendingRemoval(undefined)
  }

  const handleDatesNeeded = (ev: ChangeEvent<HTMLInputElement>) => {
    const datesNeeded = Number.parseInt(ev.target.value, 10)
    commit({ ...current, datesNeeded: Number.isFinite(datesNeeded) && datesNeeded > 0 ? datesNeeded : undefined })
  }

  const handleRespondBy = (value: Dayjs | null) => onChange({ ...element, respondBy: toRespondByInstant(value) })

  const slotLabel = (idx: number) => {
    const option = current.pollOptions[idx]
    return option && isDateSlot(option) ? formatSlotLabel(option.start) : (option?.name ?? '')
  }

  const removalCount = typeof pendingRemoval === 'number' ? answerCount(pendingRemoval) : 0
  const removalText =
    removalCount === 1
      ? `1 person has already answered for ${slotLabel(pendingRemoval ?? 0)}. Their answers for this date will be discarded.`
      : `${removalCount} people have already answered for ${slotLabel(pendingRemoval ?? 0)}. Their answers for this date will be discarded.`

  return (
    <div className={styles.root}>
      <TextField
        label="Dates needed"
        type="number"
        inputProps={{ min: 1 }}
        value={String(current.datesNeeded ?? 1)}
        onChange={handleDatesNeeded}
      />

      <DatePicker
        label="Respond by"
        value={current.respondBy ? dayjs(current.respondBy) : null}
        onChange={handleRespondBy}
      />

      {current.pollOptions.map((option, idx) =>
        option.removed ? null : (
          <div key={idx} className={styles.slotRow}>
            <DatePicker
              label="Date"
              value={option.start ? dayjs(option.start) : null}
              disabled={answerCount(idx) > 0}
              onChange={updateSlotDate(idx)}
            />
            <TimePicker
              label="Start time"
              value={option.start ? dayjs(option.start) : null}
              disabled={answerCount(idx) > 0}
              onChange={updateSlotTime(idx)}
            />
            {answerCount(idx) > 0 ? (
              <Typography variant="caption" color="textSecondary">
                {answerCount(idx) === 1 ? '1 answer' : `${answerCount(idx)} answers`}
              </Typography>
            ) : null}
            <Button onClick={handleRemoveClick(idx)}>Remove</Button>
          </div>
        )
      )}

      {hasAnyAnswers ? (
        <Typography variant="caption" color="textSecondary">
          Dates people have already answered can&apos;t be renamed — remove and re-add instead.
        </Typography>
      ) : null}

      <Button onClick={handleAdd}>Add another date</Button>

      <ConfirmDialog
        open={typeof pendingRemoval === 'number'}
        title="Remove this date?"
        text={removalText}
        okText="Remove date"
        onCancel={() => setPendingRemoval(undefined)}
        onConfirm={handleConfirmRemoval}
      />
    </div>
  )
}

export default SlotListEditor
